define([
	"dojo/_base/declare",
	"src/c8-platform/service/customViewBuilderService",
	"src/c8-platform/type/customView",
	"underscore",
	"csi",
	"csi/_base/storage",
	"csi/api",
	"csi/def/Action",
	"csi/locale",
	"csi/middleware/attributes/attributeKeysService",
	"csi/middleware/customView/customViewDataManagementService",
	"csi/middleware/customView/customViewSaveService",
	"csi/middleware/customView/customViewValidateService",
	"csi/page/localeProvider",
	"csi/prefs",
	"csi/service/viewService",
	"csi/sessionConstants",
	"csi/state",
	"csi/utility/dateUtility",
	"csi/xml",
	"./customview/ShareWith",

	"./customview/Delete",
	"./customview/Manage",
	"./customview/ManageButton",
	"./customview/Reset",
	"./customview/Save",
	"./customview/SaveAs",
	"./customview/SaveAsOnToolbar",
	"./customview/Select",
	"./customview/SetAsDefault"
], function(declare, {getValidatedCustomViewWithSourceView}, {CustomViewVersion, CUSTOM_VIEW_VERSION}, _, csi, storage, api, Action, locale,
	{validateAttributeAndHiddenKeys},
	{combineFilters, convertEmptyValueFiltersToBlankFilters, migrateFilterSettingsV1},
	{convertToPersistedValues, getCustomViewLocalStorageOptions},
	{removeInvalidAttributeKeys, validatePrintPaperAndOrientation},
	localeProvider, csiPrefs,
	{createAttributesStore},
	sessionConstants, state, {getDateTime}, xml, _CustomViewShareWithAction) {

	/*
	TODO:
	Test Slicer charts

	EditChartController, csi/slicer/ChartController - does it need to get custom view node? Maybe move views to just _CS_Data?

	Determine what to do with these - should they move here or stay in csi/view/Widget?
		getViewPreferenceKey
		prefKey -> rename to customViewKey
			csi/heading/AggregateController?
			csi/rollup/Processor - should probably use unique id for view - does not have to be persistent (widget id?)
			ChangeLogTable

		getViewPreferenceContext
		prefNodeContext - might stay in csi/view/Widget and renamed to nodeContext (to support getViewPreferenceContext for forms)
		prefViewContext
		prefViewContextOld
		prefViewContextUsed
	*/

	/*
	 * This module isolates logic necessary to manage custom views. It must be mixed-in with csi/view/Widget
	 */

	/**
	 * Determine if options from CustomView instance and options JSON the same
	 * @param {PreferenceView} cv
	 * @param {Object} options
	 * @return {boolean}
	 */
	function _areOptionsSame(cv, options) {
		return _.every(cv.customOptions, function(opt) {
			return _.isEqual(options[opt.name], cv.getOptionValue(opt));
		});
	};

	/**
	 * Resolve custom view to use as default out of given URLs
	 * @param {_CustomViewMixin} view
	 * @param {string[]} urls
	 * @param {boolean} bSiteDefault
	 * @return {Promise}
	 */
	async function _fetchDefaultView(view, urls, bSiteDefault) {
		const defaultView = view.getDefaultCustomView();
		const shareStore = csiPrefs.getShareStore();

		while (urls.length) {
			let url = urls.shift();
			let cv;

			if (url === csi.EMPTY_URL) {
				cv = defaultView;
			} else {
				const [node, sourceNode] = await getValidatedCustomViewWithSourceView(url);
				cv = view.newCustomViewFromNode(node, sourceNode);
			}

			// since URLs here can come from user default views, and those defaults could be out of date with rest of store,
			// here we need to check that view is still valid for user
			if (csiPrefs.canSelectView(cv, bSiteDefault) && _isValidViewForUser(cv, shareStore)) {
				return cv;
			}
		}

		// load default view if no other options for default view
		return defaultView;
	}

	/**
	 * Return true if given view is accessible by current user
	 * @param {PreferenceView} cv
	 * @param {Names} shareStore
	 * @return {boolean}
	 */
	function _isValidViewForUser(cv, shareStore) {
		// 1. admin user can see all views
		// 2. all users can see system wide views
		// 3. _canSelectView already checks that personal (not shared) view is for the current user
		if (state.get("admin") || cv.isSystemWide() || !cv.isShared()) {
			return true;
		}

		// return true if any of the parties with which custom view is shared are applicable to this user
		return _.some(cv.getParty(), function(url) {
			return !!shareStore.get(url);
		});
	}

	return declare(null, {
		/** @property {boolean} manageSingleCustomView - if set to true then hide left pane and Display Options tab */
		//manageSingleCustomView: false,

		/** @property{PreferenceView} customViewApplied - custom view applied to view */
		customViewApplied: null,

		/** @property{cnl} customViewContainerUrl - container url of the custom view */
		//customViewContainerUrl: null,

		// String: id of selected custom view
		//customViewId: null,

		// String: data store containing custom views for view
		//customViewContext: null,

		// Boolean: indicates if custom view settings have changed since selected
		customViewDirty: false,

		/**
		 * @property{string} customViewMigrationContainerKey - preference container key (prefKey) set only when migrating custom view for this view;
		 * this is necessary when key contains extra context for the view, and view is not loaded in usual fashion with root node
		 */
		//customViewMigrationContainerKey: null,

		/** @property{string} customViewPathHints - path hints used for custom views in container */
		//customViewPathHints: null,

		/** @property{CustomViews} customViewStore - data store containing custom views for view */
		//customViewStore: null,

		/** @property{boolean} customViewStoreRefreshed - custom view store was modified from some other view */
		//customViewStoreRefreshed: false,

		/** @property {boolean} disableCustomViewToolbar - set to true to disable creation of custom views (i.e. just want local storage for view settings) */
		//disableCustomViewToolbar: false,

		/** @property {boolean} enableCustomViewDialog - set to false if view is fully configurable on toolbar */
		enableCustomViewDialog: true,

		/** @property {C8AttributeKey} flatKeyToInsertIfGroupFirst - attribute key to insert into view if first one is a group (restriction only exists for table views) */
		//flatKeyToInsertIfGroupFirst: null,

		/** @property {ICustomViewAttribute[]} hiddenKeys - keys of attributes that are hidden from the view */
		//hiddenKeys: null,

		/** @property {string} insertCustomViewPluginsAfter - name of toolbar plugin where to insert custom view plugins (default is at front) */
		//insertCustomViewPluginsAfter: "",

		// Boolean: if set to true then view is loading from custom view as opposed to responding to action to alter view
		//loadingCustomView: false,

		// String: class for view preferences - if null, then view does not support custom views
		//prefClass: null,

		// Boolean: indicates if should only display custom views for site and operate on site default view, if true, the selected custom view automatically becomes the site default custom view
		//prefSiteOnly: false,

		//Boolean: indicates if we are printing the current view
		//printCurrentView: false,

		// String: override default custom view
		//urlDefaultView: null,

		//Boolean: indicates if the watching of custom view options were set up
		//_customViewOptionsWatched: false,

		// PreferenceView: custom view containing default values for all options
		//_defaultCustomView: null,

		//Boolean: indicates if we are in the process of changing CustomView instance
		//_settingCustomView: false,

		/**
		 * Add ad-hoc custom view option after default custom view has already been created;
		 * when printing, the default custom view may not exist
		 * @param {string} prop
		 * @param {any} defaultValue
		 */
		addAdHocCustomViewOption(prop, defaultValue) {
			this[prop] = defaultValue;
			this._defaultCustomView?.addCustomOption(prop, defaultValue);
		},

		/**
		 * Return attribute level parameters for custom view, if any
		 * @return {CustomViewAttributesLevelParams[]}
		 */
		getCustomViewAttributeLevels() {
			const {specBuilder} = this;
			if (specBuilder) {
				return [{specBuilder}];
			}
		},

		/**
		 * Return store of available node contexts to use for custom views, if any;
		 * for example, return store of product types to distinguish default views from one type to another
		 * @return {Store}
		 */
		getCustomViewNodeContextsStore: function() {
			return null;
		},

		/**
		 * Return properties to render preview in custom view dialog
		 * @return {DojoViewProps}
		 */
		getCustomViewPreviewProps() {
			const {cacheView, defView, relRoot, urlRoot} = this;
			const previewProperties = this.getPreviewProperties();

			return {
				forceReadOnly: true,
				nodeUrl: urlRoot,
				onViewCreated: (view) => {
					// actions and wizards may add to the view's normal query filters, so ensure that
					// those changes are reflected in the preview
					const viewRel = view.relRoot;
					if (viewRel) {
						viewRel.m_filters = relRoot?.m_filters?.map((f) => f.cloneNode(true));
						viewRel.m_query = relRoot?.m_query?.copy();
					}
				},
				viewId: defView.m_id,
				viewParams: {
					...previewProperties,
					cacheView,
					isPreview: true,
				}
			};
		},

		/**
		 * Return view preferences from view configuration; allow derived classes to override
		 * @return {PreferenceView}
		 */
		getDefaultCustomView: function() {
			if (this._defaultCustomView) {
				return this._defaultCustomView;
			}

			if (!this.prefClass) {
				return null;
			}

			const rc = this.defView.getPageClass();
			const ctr = csi.syncRequire(this.prefClass);
			const cv = this._defaultCustomView = new ctr({
				targetClass: rc?.type ?? "",
				urlNode: csi.EMPTY_URL
			});

			if (this.querySizeFixed) {
				cv.removeCustomOption("querySize");
			}

			cv.customOptions?.forEach((opt) => {
				cv.setValue(opt.name, cv.getOptionValueFromValue(opt, this.get(opt.name)));
			});

			this.onCreateDefaultCustomView(cv);

			return cv;
		},

		/**
		 * Return the current custom view option values for the view
		 * @param {Object} [values] - if passed, then assign values to given object
		 * @return {Object}
		 */
		getModifiedCustomViewValues: function(values) {
			// the selected custom view could be null when the previously selected view was un-shared and the custom view
			// dialog is opened - leaving no view for the user; in such a case (rare), use default view
			const cv = this.getSelectedCustomView() || this.getDefaultCustomView();
			const obj = values || cv.copyValues();

			return csi.hashArray(cv.customOptions, "name", opt => {
				const currentValue = opt.transient ? cv.getOptionValue(opt) : this.get(opt.name);
				return cv.getOptionValueFromValue(opt, currentValue);
			}, obj);
		},

		/**
		 * Return custom view applied to view
		 * @return {PreferenceView}
		 */
		getSelectedCustomView: function() {
			return this.customViewApplied;
		},

		/**
		 * Return true if custom view supports given option
		 * @param {string} id
		 * @return {boolean}
		 */
		hasCustomViewOption: function(id) {
			var cv = this.getDefaultCustomView();
			return cv && cv.hasOption(id);
		},

		/**
		 * Return true if selected custom view is the default for the party for which it is shared
		 * @return {boolean}
		 */
		isDefaultCustomView: function() {
			const cv = this.getSelectedCustomView();
			if (!cv) {
				return true;
			}

			const context = this.get("customViewContext");
			const party = this.prefSiteOnly ? csi.EMPTY_URL : (sessionConstants.roleUrl || csi.EMPTY_URL);
			return !cv.isCurrentUser() && (csiPrefs.getDefaults().get(context, party) == this.customViewId);
		},

		/**
		 * Load and migrate custom view from given _CS_PreferenceView node
		 * @param {C8Node} node 
		 * @param {C8Node} [sourceNode]
		 * @return {PreferenceView}
		 */
		newCustomViewFromNode(node, sourceNode) {
			const defaultCustomView = this.getDefaultCustomView();

			if (!node) {
				return defaultCustomView.copy();
			}

			const sourceView = sourceNode ? this.newCustomViewFromNode(sourceNode) : null;
			const cv = defaultCustomView.newInstance();

			cv.load(node, sourceView);
			this._migrateAndValidateCustomViewValues(cv.versionOnNode, cv.values);

			return cv;
		},

		/**
		 * Extension point to react to change to custom view, usually used to reset runtime view properties
		 */
		onChangeCustomView() { },

		/**
		 * Extension point for when default view may have changed
		 */
		onChangeDefaultCustomView() { },

		/**
		 * Extension point for when default custom view is created
		 * @param {PreferenceView} cv
		 */
		onCreateDefaultCustomView(cv) { },

		/**
		 * Extension point to populate options for attributes store
		 * @param {CustomViewAttributesOptions} options
		 */
		onCustomViewAttributeOptions(options) {
			if (!this.hasCustomViewOption("attributeKeys")) {
				options.fixed = true;
			}
		},

		/**
		 * Extension point to populate options for data management store
		 * @param {DataManagementOptions} options
		 */
		onCustomViewDataManagementOptions(options) { },

		/**
		 * @override
		 * Refresh custom view if store was changed while hidden
		 */
		onTabSwitch() {
			if (this.customViewStoreRefreshed) {
				this.customViewStoreRefreshed = false;
				this.refreshDefaultCustomView(true);
			}

			this.inherited(arguments);
		},

		/**
		 * Setup view to support custom views if so configured
		 */
		postMixInProperties: function() {
			// disable custom views if container key is explicitly empty
			if (this.defView.prefKey === "") {
				this.prefClass = null;
			}

			this.inherited(arguments);

			const {
				disableCustomViewToolbar,
				enableCustomViewDialog,
				insertCustomViewPluginsAfter,
				plugins,
				prefClass,
				prefSiteOnly,
				printCurrentView
			} = this;

			if (!prefClass || printCurrentView) {
				return;
			}

			const store = this.refreshCustomViewStore();

			if (store && !disableCustomViewToolbar && !state.get("print")) {
				// initialize drop-down actions allowed for store
				const children = [
					"CustomViewSave",
				];

				if (!enableCustomViewDialog) {
					children.push("CustomViewSaveAsOnToolbar");
				}

				if (enableCustomViewDialog) {
					children.push("CustomViewSaveAs");
					children.push("CustomViewManage");
				} else if (csiPrefs.hasAdminAccess()) {
					if (!prefSiteOnly) {
						children.push("|");
					}

					if (!prefSiteOnly) {
						const userUrl = sessionConstants.userUrl;
						const shareStore = csiPrefs.getShareStore();
						const shareChildren = shareStore.query().map(function(item) {
							let name = item[shareStore.labelProperty];
							const party = shareStore.getIdentity(item);

							// rename "All" and "None" to "System Wide" and "Personal";
							if (party === csi.EMPTY_URL) {
								name = locale.resource("CustomViewSystemWide");
							} else if (party === userUrl) {
								name = locale.resource("CustomViewPersonal");
							}

							return _.extend(new _CustomViewShareWithAction(), {
								m_id: "CustomViewShareWith_" + name,
								m_name: name,
								shareWith: party
							});
						});

						children.push({
							name: "CustomViewShare",
							children: this._modifyShareChildren(shareChildren)
						});
					}

					if (!prefSiteOnly) {
						children.push("CustomViewDefault");
					}
				}

				children.push("|");
				children.push("CustomViewReset");
				children.push("CustomViewDelete");

				// insert into toolbar plugins
				let n = 0;

				if (insertCustomViewPluginsAfter) {
					n = _.findIndex(plugins, p => {
						return (p === insertCustomViewPluginsAfter) ||
							(p && p.pluginName === insertCustomViewPluginsAfter) ||
							(p && p.m_id === insertCustomViewPluginsAfter);
					});

					if (n === -1) {
						n = 0;
					} else {
						++n;
					}
				}

				const customViewActions = {
					name: "CustomViewActions",
					children: children
				};

				plugins.splice(n, 0, customViewActions, "CustomViewSelect");
			}
		},

		/**
		 * Refresh custom view store to work with this view
		 * @param {boolean} [bResetStore] - set to true to re-query custom views from server
		 * @return {CustomViews}
		 */
		refreshCustomViewStore: function(bResetStore) {
			const cv = this.getDefaultCustomView();

			if (cv) {
				const viewPreferenceKey = this.getViewPreferenceKey();
				const store = csiPrefs.getStore(viewPreferenceKey, cv, bResetStore);
				this._set("customViewStore", store);

				// populate the container
				this.customViewContainerUrl = csiPrefs.getContainerUrl(viewPreferenceKey);
				return store;
			}

			return null;
		},

		/**
		 * View settings determining default custom view and/or custom view store have changed, so reset
		 * to default custom view (and optionally store), and re-render view
		 * @param {boolean} [bRefreshStore] - set to true to ensure that default view for store is correct
		 * @param {boolean} [bResetStore] - set to true to re-query custom views from server
		 * @return {Promise}
		 */
		refreshDefaultCustomView: function(bRefreshStore, bResetStore) {
			this.onChangeCustomView();

			if (bRefreshStore) {
				this.set("customViewDirty", false);
				this.refreshCustomViewStore(bResetStore);
			}

			return this._load(this._renderDefaultCustomView)?.then(() => {
				if (bRefreshStore) {
					this.onChangeDefaultCustomView();
				}
			});
		},

		/**
		 * Set new values for attribute and hidden keys in view
		 * @param {ICustomViewAttribute[]} attributeKeys 
		 * @param {ICustomViewAttribute[]} hiddenKeys 
		 */
		setAttributeAndHiddenKeys(attributeKeys, hiddenKeys) {
			const [newAttributeKeys, newHiddenKeys] = validateAttributeAndHiddenKeys(attributeKeys, hiddenKeys, this.flatKeyToInsertIfGroupFirst);
			this.set("attributeKeys", newAttributeKeys);
			this.set("hiddenKeys", newHiddenKeys);
		},

		/**
		 * @override
		 * Return true if view configured with a preferences class (_CS_PreferenceView or derived) and has toolbar controls to save it
		 */
		supportsCustomViews: function() {
			return this.prefClass && !this.disableCustomViewToolbar;
		},

		uninitialize: function() {
			// summary: release custom view store from use in this view
			// FIX: this is needed since store items reference defView which would be nice to avoid
			if (this.customViewStore) {
				csiPrefs.releaseStore(this.customViewStore);
			}

			this.inherited(arguments);
		},

		/**
		 * Apply values from selected custom view
		 * @param {PreferenceView} cv
		 */
		_applyCustomView: function(cv) {
			const node = this.getViewNode();
			const context = this.get("customViewContext");
			const options = storage.get(context);
			let dirty = false;
			let values = cv.values;

			if (options) {
				// throw away any local storage that was saved without a version number; without it, we can't know
				// what to migrate
				const localStorageOptions = getCustomViewLocalStorageOptions(options);
				const runtimeValues = {};
				const version = options.VR;

				if (version && cv.convertToRuntimeValues(options, runtimeValues, localStorageOptions)) {
					// transform values based on any validation/migration requirements
					this._migrateAndValidateCustomViewValues(version, runtimeValues);

					// determine if options from local storage differ from saved custom view
					dirty = !_areOptionsSame(cv, runtimeValues);
					values = runtimeValues;
				} else {
					// remove application storage for custom view as it no longer applies, or is no different than the saved view
					storage.remove(context);
				}
			}

			this._settingCustomView = true;	// could possibly rely on loadingCustomView instead?
			this._set("customViewApplied", cv);
			this._set("customViewId", cv.urlNode);

			// update dirty flag - it is possible that an option is changed back to the original
			// value which makes the view "clean" again
			if (dirty != this.customViewDirty) {
				this._set("customViewDirty", dirty);
			}

			// apply option values to widget
			_.each(values, (value, key) => {
				this._set(key, value);
			});

			// support pre-5.2 instances with PaperSize attribute per instance
			if (this.bPrintView && node && node.PaperSize && !this.printPaper && !this.printOrientation) {
				var oldPaper = xml.enumGetText(node.PaperSize);
				if (oldPaper) {
					this.set("printPaper", oldPaper);
					this.set("printOrientation", api.LANDSCAPE);
				}
			}

			// notify others when view preferences change
			this.onViewPreferences();

			// indicates that view needs to check headings for unloaded renderers
			this._bHeadingsDirty = true;

			this._setWatchOptions(cv);
			this._settingCustomView = false;
		},

		/**
		 * Calculate value for customViewContext if not specified
		 * @return {string}
		 */
		_getCustomViewContextAttr: function() {
			// TODO: Only calculate context when it is null, and it would become null when prefKey, prefNodeContext or prefViewContext changes;
			// would need to use "set" function for those 3 properties, and fix any overrides to getViewPreferenceKey (i.e. ChangeLogTable)
			const value = this.getViewPreferenceContext();
			this._set("customViewContext", value);
			return value;
		},

		/**
		 * Return array of hidden keys
		 * @return {string[]}
		 */
		_getHiddenKeysAttr: function() {
			return this.hiddenKeys || [];
		},

		/**
		 * Find candidate URLs to load as default, and then return first one that is still available to current user
		 * @return {Promise<PreferenceView | void>}
		 */
		loadDefaultCustomView: async function() {
			if (!this.customViewStore) {
				return;
			}

			const {
				defView,
				prefSiteOnly,
				prefViewContextOld,
				urlDefaultView,
			} = this;

			var defaults = csiPrefs.getDefaults();
			var viewNode = this.getViewNode();
			var idContext;
			var urls = [];
			var url;
			var urlParty;

			const testOldContext = (prefViewContextOld) => {
				// handle previous method of generating context
				var idContextOld = csiPrefs.composeKey(this.getViewPreferenceKey(), prefViewContextOld);
				var urlOld = defaults.get(idContextOld, urlParty);

				if (xml.isValidURL(urlOld)) {
					this.prefViewContextUsed = idContextOld;
					idContext = idContextOld;
					return urlOld;
				}

				return csi.EMPTY_URL;
			}

			// reset view context used before refreshing
			this.prefViewContextUsed = null;

			idContext = this.getViewPreferenceContext();

			// if custom view is specified explicitly, then use it
			if (urlDefaultView) {
				urls.push(urlDefaultView);
			}

			// if working with only site-wide default views, then pass EMPTY_URL as party for site views
			urlParty = prefSiteOnly ? csi.EMPTY_URL : null;

			// next possible default is site/user default - if printing approval PDF then use shared default custom
			// view, otherwise use user's current default view
			url = defaults.get(idContext, urlParty);

			if (!xml.isValidURL(url)) {
				// handle previous methods of generating context
				if (prefViewContextOld) {
					url = testOldContext(prefViewContextOld);
				}

				// in previous releases (5.3.5 and before), the derived class was used as a view context for views defined for a base class
				if (!xml.isValidURL(url) && viewNode && (viewNode.$Type != defView.getTypeNode())) {
					url = testOldContext(viewNode.$Type);
				}
			}

			urls.push(url);

			// it is possible that a user can reference a custom view that they should no longer be able to access -
			// in that case, they should switch back to the default view
			if (!prefSiteOnly) {
				// add default shared view by role
				const urlRole = sessionConstants.roleUrl;
				if (urlRole) {
					if (url = defaults.get(idContext, urlRole)) {
						urls.push(url);
					}
				}
				// also add default view for site, if it exists
				const urlSite = csiPrefs.getDefaults().get(idContext, csi.EMPTY_URL, true);
				if (urlSite) {
					urls.push(urlSite);
				}
			}

			// load default views one by one until found
			const cv = await _fetchDefaultView(this, urls, csiPrefs.hasSiteDefaultForContext(idContext));
			if (cv) {
				this._applyCustomView(cv);
				return cv;
			}
		},

		/**
		 * Modify values from custom view due to migration or validation before applying to the view
		 * @param {number} version 
		 * @param {ICustomViewOptions} values 
		 */
		_migrateAndValidateCustomViewValues(version, values) {
			const {flatKeyToInsertIfGroupFirst, specBuilder} = this;
			const {attributeKeys, filterSettings, hiddenKeys} = values;

			validatePrintPaperAndOrientation(values);

			// ensure attributeKeys and hiddenKeys are in sync and valid for view
			if (attributeKeys) {
				const [newAttributeKeys, newHiddenKeys] = validateAttributeAndHiddenKeys(attributeKeys, hiddenKeys, flatKeyToInsertIfGroupFirst);
				const attributesStore = createAttributesStore(this, localeProvider, true);
				values.attributeKeys = removeInvalidAttributeKeys(newAttributeKeys, attributesStore);
				values.hiddenKeys = removeInvalidAttributeKeys(newHiddenKeys, attributesStore, true);
			}

			if (filterSettings) {
				if (version < CustomViewVersion.V79) {
					// combine filters if they have the same attribute and operator
					_.each(values.filterSettings, (filterObj) => combineFilters(filterObj, specBuilder));
				}

				if (version < CustomViewVersion.V791) {
					// convert filters with group attributes to filter group objects
					values.filterSettings = migrateFilterSettingsV1(values.filterSettings, specBuilder);
				}

				if (version < CustomViewVersion.V710) {
					// convert empty value filters to blank / not blank filters for custom view before 7.10
					values.filterSettings = convertEmptyValueFiltersToBlankFilters(values.filterSettings, specBuilder);
				}
			}
		},

		/**
		 * Reorder the array and add menu separator
		 * @param {Action[]} arr
		 * @return {(Action|string)[]}
		 */
		_modifyShareChildren: function(arr) {
			const userUrl = sessionConstants.userUrl;
			const tempArray = [];
			const filterArray = _.filter(arr, function(item) {
				const shareWith = item.shareWith;
				if (shareWith === csi.EMPTY_URL) {
					tempArray.push(item);
					tempArray.push("|");
				} else if (shareWith === userUrl) {
					tempArray.unshift("|");
					tempArray.unshift(item);
				} else {
					return item;
				}
			});
			return tempArray.concat(filterArray);
		},

		/**
		* React to change to custom view option
		* @param {string} attrName
		* @param {any} oldValue
		* @param {any} newValue
		*/
		_onCustomViewOptionChanged(attrName, oldValue, newValue) {
			// dojo/Stateful does an exact comparison, so for array values it could give false notification
			if (!this._settingCustomView && !_.isEqual(oldValue, newValue)) {
				// check if the local version of custom view modified relative to the server version
				const cv = this.getSelectedCustomView();

				// CV is null when CV deleted and new one is not yet loaded - don't process changes for deleted CV as it causing errors
				if (!cv) {
					return;
				}

				const currentValues = this.getModifiedCustomViewValues();
				const dirty = !_areOptionsSame(cv, currentValues);

				if (dirty) {
					// save current values to local storage
					const persistedValues = {};
					const keyMap = convertToPersistedValues(currentValues, persistedValues, cv.keys);

					// save URL so that if selected custom view changes before coming back, the options will not be applied;
					// save timestamp for change which will be compared against timestamp of saved custom view
					persistedValues.CV = cv.urlNode;
					persistedValues.TS = getDateTime();

					// save version number so that local storage values can be migrated too
					persistedValues.VR = CUSTOM_VIEW_VERSION;

					// persist keys in local storage too as one of values since not saving whole node
					persistedValues.KEYS = keyMap;

					storage.set(this.get("customViewContext"), persistedValues);
				}

				this.set("customViewDirty", dirty);
			}
		},

		/**
		 * The custom view store that matches this view's key was changed in some view, so react accordingly
		 * @param {ViewWidget} view
		 * @param {cnl[]} modifiedUrls - list of custom view urls that were modified
		 */
		_onCustomViewStoreChanged(view, modifiedUrls) {
			if (view === this) {
				return;
			}

			if (modifiedUrls.includes(this.customViewId)) {
				// if selected custom view was modified or deleted, then refresh the view when view comes back into view
				this.customViewStoreRefreshed = true;
			} else {
				// otherwise, just update store so it will be current next time it is used
				this.refreshCustomViewStore();
			}
		},

		/**
		 * Extension point for adjusting ChangeNode after saving Custom View
		 * @param {ChangeNode} cn 
		 * @param {ICustomViewOptions} options
		 */
		onSaveCustomView(cn, options) { },

		/**
		 * @override
		 * Before rendering view, apply default custom view; when printing ad-hoc, the properties are transferred from the source view (see PrintService)
		 */
		_onStartup: function() {
			// register as an observer of the custom view store
			this.own(
				csiPrefs.observeCustomViewStore(this.getViewPreferenceKey(), (view, modifiedUrls) => this._onCustomViewStoreChanged(view, modifiedUrls))
			);

			if (this.printCurrentView) {
				this.inherited(arguments);
			} else {
				this._renderDefaultCustomView();
			}
		},

		/**
		 * Determine custom view to load and start loading view
		 * @return {Promise}
		 */
		async _renderDefaultCustomView() {
			this.set("loadingCustomView", true);

			if (this.isPreview) {
				// when previewing, all custom view options are already applied; so only inform view that options are applied 
				// and indicate renderers need to be loaded if new keys in view
				this.onViewPreferences();
				this._bHeadingsDirty = true;
			} else {
				// load default custom view and apply options from it
				await this.loadDefaultCustomView();
				this.urlDefaultView = null;
			}

			return this.when(this._renderView(), () => {
				this.set("loadingCustomView", false);
			});
		},

		_setCustomViewDirtyAttr: function(/*Boolean*/dirty) {
			// summary: remove local storage if no longer dirty
			this._set("customViewDirty", dirty);

			if (!dirty) {
				storage.remove(this.get("customViewContext"));
			}
		},

		_setWatchOptions: function(/*PreferenceView*/cv) {
			// summary: set up watch for custom view options
			if (!this._customViewOptionsWatched) {
				_.each(cv.customOptions, opt => {
					this.own(
						this.watch(opt.name, this._onCustomViewOptionChanged.bind(this))
					);
				});

				this._customViewOptionsWatched = true;
			}
		}
	});
});
